import { useEffect, useRef, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

const SCROLL_POLL_INTERVAL = 50;
const STABLE_THRESHOLD = 12;

const NOOP_CLEANUP = () => {};

const DESKTOP_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
  inline: 'center',
};

const MOBILE_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'start',
  inline: 'center',
};

type ScrollContainer = HTMLElement | Window;

interface ScrollPosition
  extends Readonly<{
    scrollTop: number;
    scrollLeft: number;
  }> {}

/**
 * Gets the current scroll position of a container (or window if container is null)
 */
const getScrollPosition = (container: ScrollContainer): ScrollPosition =>
  container instanceof HTMLElement
    ? { scrollTop: container.scrollTop, scrollLeft: container.scrollLeft }
    : {
        scrollTop: container.pageYOffset || container.document.documentElement.scrollTop,
        scrollLeft: container.pageXOffset || container.document.documentElement.scrollLeft,
      };

/**
 * Hook to scroll an element into view when it changes.
 *
 * Automatically scrolls to the target element with configurable scroll behavior.
 * Tracks scroll completion to prevent UI updates during scroll animation.
 *
 * Features:
 * - Detects scroll completion by monitoring scroll position stability
 * - Prevents redundant scrolls to the same element
 * - Proper cleanup of intervals and state
 * - Mobile: scrolls to top (block: 'start'), Desktop: scrolls to center (block: 'center')
 *
 * @param element - The HTML element to scroll to, or null/undefined to disable
 * @param options - Optional scroll behavior configuration (overrides mobile/desktop defaults)
 * @param scrollContainer - Optional scroll container to track scroll position; defaults to window
 * @returns Object with `isScrolling` boolean indicating if scroll is in progress
 *
 * @example
 * const { isScrolling } = useScrollToElement(targetElement);
 * // Wait for scroll to complete before showing UI
 * {!isScrolling && <Tooltip />}
 */
export const useScrollToElement = (element: HTMLElement | null | undefined, options?: ScrollIntoViewOptions, scrollContainer?: HTMLElement | null): { isScrolling: boolean } => {
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrolledRef = useRef<HTMLElement | null>(null);
  const lastScrollContainerRef = useRef<HTMLElement | null | undefined>(undefined);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollOptionsRef = useRef(options);
  const isMobile = useIsMobileScreen();

  // Update scroll options ref without triggering effect
  useEffect(() => {
    scrollOptionsRef.current = options;
  }, [options]);

  useEffect(() => {
    // No element - reset state
    if (!element) {
      lastScrolledRef.current = null;
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsScrolling(false);
      return;
    }

    // Same element and container - don't scroll again
    if (lastScrolledRef.current === element && lastScrollContainerRef.current === scrollContainer) {
      setIsScrolling(false);
      return;
    }

    // Clear any existing polling interval before starting new scroll
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    lastScrolledRef.current = element;
    lastScrollContainerRef.current = scrollContainer;

    const cleanup = withWindow(global => {
      try {
        const container: ScrollContainer = scrollContainer ?? global;

        // Start scrolling
        setIsScrolling(true);
        const scrollOpts = scrollOptionsRef.current || (isMobile ? MOBILE_SCROLL_OPTIONS : DESKTOP_SCROLL_OPTIONS);
        element.scrollIntoView(scrollOpts);

        // Monitor scroll position to detect completion
        let lastPosition = getScrollPosition(container);
        let stableCount = 0;

        pollIntervalRef.current = setInterval(() => {
          const currentPosition = getScrollPosition(container);

          // Check if scroll position has stabilized
          if (currentPosition.scrollTop === lastPosition.scrollTop && currentPosition.scrollLeft === lastPosition.scrollLeft) {
            stableCount++;

            // Scroll is complete - position stable for STABLE_THRESHOLD frames
            if (stableCount >= STABLE_THRESHOLD) {
              setIsScrolling(false);
              if (pollIntervalRef.current !== null) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
            }
          } else {
            // Scroll position changed - reset stability counter
            stableCount = 0;
            lastPosition = currentPosition;
          }
        }, SCROLL_POLL_INTERVAL);

        // Return cleanup function for the interval
        return () => {
          if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        };
      } catch (error) {
        // Scroll failed - reset state
        console.warn('Failed to scroll element into view.', error);
        setIsScrolling(false);
        if (pollIntervalRef.current !== null) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return NOOP_CLEANUP;
      }
    }, NOOP_CLEANUP);

    return cleanup;
  }, [element, isMobile, scrollContainer]);

  return { isScrolling };
};
